import React, { useEffect, useRef, useState } from "react";
import { Download, Upload, FileText, MapPin, Trash2 } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { Btn, Select, Toolbar } from "@/components/Primitives";
import { api, API, fmtNumber, formatApiErrorDetail, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function textOn(hex) {
  const h = (hex || "#888888").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return "#171717";
  return 0.299 * r + 0.587 * g + 0.114 * b < 140 ? "#ffffff" : "#171717";
}

const STATUS_COLS = [
  { key: "raw", label: "Raw" },
  { key: "awaiting_paint", label: "Awaiting Paint" },
  { key: "available", label: "Available" },
  { key: "allocated", label: "Allocated" },
];

function OffsiteMatrix({ data }) {
  if (!data) return <div className="panel p-10 text-center text-stone-400 text-sm">Loading breakdown…</div>;
  if (!data.bands.length) {
    return <div className="panel p-10 text-center text-stone-400 text-sm" data-testid="offsite-empty">No offsite stock in this view. Upload an Excel file or switch the Pointed / Not Pointed toggle.</div>;
  }
  const g = data.grand_total;
  return (
    <div className="panel overflow-x-auto" data-testid="offsite-matrix">
      <table className="min-w-full text-sm border-collapse">
        <thead>
          <tr className="bg-stone-800 text-white">
            {["Butt Class Ø", "Length", "Butt Class Ø", "Butt Ø (mm)", "Code", "Colour", "Order #", "Raw", "Awaiting Paint", "Available", "Allocated", "Damaged", "Total", "CBM", "Calc Wt (t)", "CBM / Pole", "Wt / Pole (kg)"].map((h, i) => (
              <th key={i} className="px-3 py-2 text-center text-xs font-bold uppercase tracking-wider border border-stone-600 whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.bands.map((band, bi) => {
            const bg = band.swatch || "#888888";
            const fg = textOn(bg);
            return (
              <React.Fragment key={bi}>
                {band.rows.map((r, ri) => (
                  <tr key={`${bi}-${ri}`} style={{ background: bg, color: fg }} className="font-semibold">
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.class_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.length_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.class_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.avg_label}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono">{r.code}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 uppercase tracking-wider">{band.colour}</td>
                    <td className="px-3 py-1.5 text-center border border-stone-300/40 font-mono text-xs">{r.order_numbers || "—"}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.raw, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.awaiting_paint, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.available, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.allocated, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.damaged, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono font-bold">{fmtNumber(r.qty, 0)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.cbm, 2)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.weight, 2)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.cbm_per_pole, 3)}</td>
                    <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(r.weight_per_pole, 1)}</td>
                  </tr>
                ))}
                <tr style={{ background: bg, color: fg }} className="font-bold border-t-2 border-white">
                  <td className="px-3 py-1.5 border border-stone-300/40" colSpan={7}>{band.total_label || band.colour} — Total</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_raw, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_awaiting_paint, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_available, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_allocated, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_damaged, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_qty, 0)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_cbm, 2)}</td>
                  <td className="px-3 py-1.5 text-right border border-stone-300/40 font-mono">{fmtNumber(band.total_weight, 2)}</td>
                  <td className="px-3 py-1.5 border border-stone-300/40" colSpan={2} />
                </tr>
                <tr><td colSpan={17} className="h-2 bg-white" /></tr>
              </React.Fragment>
            );
          })}
          <tr className="bg-emerald-900 text-white font-bold" data-testid="offsite-grand-total">
            <td className="px-3 py-2 border border-emerald-800" colSpan={7}>GRAND TOTAL</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.raw, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.awaiting_paint, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.available, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.allocated, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.damaged, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.qty, 0)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.cbm, 2)}</td>
            <td className="px-3 py-2 text-right border border-emerald-800 font-mono">{fmtNumber(g.weight, 2)}</td>
            <td className="px-3 py-2 border border-emerald-800" colSpan={2} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function OffsiteStock() {
  const { user } = useAuth();
  const canEdit = can(user?.role, "offsite:create");
  const [locations, setLocations] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [location, setLocation] = useState("__all__");
  const [breakdown, setBreakdown] = useState(null);
  const [pointedView, setPointedView] = useState("pointed");
  const [mode, setMode] = useState("append");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadLocations = async () => {
    const r = await api.get("/offsite/locations");
    setLocations(r.data.locations || []);
    setTotalQty(r.data.total_qty || 0);
  };

  const loadBreakdown = async () => {
    const params = { pointed: pointedView === "pointed" };
    if (location !== "__all__") params.location = location;
    const r = await api.get("/offsite/breakdown", { params });
    setBreakdown(r.data);
  };

  useEffect(() => { loadLocations(); }, []);
  useEffect(() => { loadBreakdown(); }, [location, pointedView]);

  const downloadTemplate = () => window.open(`${API}/offsite/template.xlsx`, "_blank");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mode === "replace" && !confirm("Replace ALL offsite stock with this file? This cannot be undone.")) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/offsite/upload?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${mode === "replace" ? "Replaced with" : "Added"} ${r.data.inserted} row(s)`);
      await loadLocations();
      await loadBreakdown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLocation = async () => {
    const label = location === "__all__" ? "ALL offsite stock" : `offsite stock at "${location}"`;
    if (!confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      const r = await api.delete("/offsite", { params: location === "__all__" ? {} : { location } });
      toast.success(`Deleted ${r.data.deleted} row(s)`);
      setLocation("__all__");
      await loadLocations();
      await loadBreakdown();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const locParam = location === "__all__" ? "" : `?location=${encodeURIComponent(location)}`;

  return (
    <AppLayout>
      <PageHeader
        title="Offsite Stock"
        subtitle="Stock held at other locations. Separate from your live warehouse — upload to view its code breakdown."
        actions={
          <div className="flex items-center gap-2">
            <Btn variant="secondary" as="a" href={`${API}/offsite/breakdown.xlsx${locParam}`} target="_blank" testid="offsite-export-xlsx">
              <Download className="h-4 w-4" /> Excel
            </Btn>
            <Btn variant="secondary" as="a" href={`${API}/offsite/breakdown.pdf${locParam}`} target="_blank" testid="offsite-export-pdf">
              <Download className="h-4 w-4" /> PDF
            </Btn>
          </div>
        }
      />

      {canEdit && (
        <div className="panel p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <Btn variant="secondary" onClick={downloadTemplate} testid="offsite-download-template">
              <FileText className="h-4 w-4" /> Download Template
            </Btn>
            <div className="flex items-center gap-2">
              <label className="text-sm text-stone-500">Mode</label>
              <Select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="offsite-upload-mode" className="max-w-[190px]">
                <option value="append">Append to existing</option>
                <option value="replace">Replace all</option>
              </Select>
            </div>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="offsite-file-input" />
            <Btn onClick={() => fileRef.current?.click()} disabled={uploading} testid="offsite-upload-btn">
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload Excel"}
            </Btn>
          </div>
        </div>
      )}

      <Toolbar>
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-stone-400" />
          <Select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="offsite-location-filter" className="max-w-[280px]">
            <option value="__all__">All Locations ({fmtNumber(totalQty, 0)} pcs)</option>
            {locations.map((l) => (
              <option key={l.location} value={l.location}>{l.location} ({fmtNumber(l.qty, 0)} pcs)</option>
            ))}
          </Select>
        </div>
        <div className="text-xs text-stone-500 ml-auto font-mono">
          {locations.length} location(s) · {fmtNumber(totalQty, 0)} pcs total
        </div>
        {canEdit && locations.length > 0 && (
          <Btn variant="danger" onClick={clearLocation} testid="offsite-clear-btn">
            <Trash2 className="h-4 w-4" /> {location === "__all__" ? "Clear All" : "Clear Location"}
          </Btn>
        )}
      </Toolbar>

      <div className="inline-flex rounded-sm border border-stone-300 overflow-hidden mb-4" data-testid="offsite-pointed-toggle">
        {[{ key: "pointed", label: "Pointed" }, { key: "not_pointed", label: "Not Pointed" }].map((t) => (
          <button
            key={t.key}
            onClick={() => { setBreakdown(null); setPointedView(t.key); }}
            className={`px-4 py-2 text-sm font-medium transition-colors ${pointedView === t.key ? "bg-stone-800 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
            data-testid={`offsite-toggle-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <OffsiteMatrix data={breakdown} />
    </AppLayout>
  );
}
